import { Injectable, Optional } from '@angular/core';
import { Auth, authState, User as FirebaseUser } from '@angular/fire/auth';
import { Firestore, doc, updateDoc } from '@angular/fire/firestore';
import { Messaging, getMessaging, getToken, onMessage, MessagePayload } from '@angular/fire/messaging';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular/standalone';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { take, tap } from 'rxjs/operators';

interface User extends FirebaseUser {
  tokens?: { [key: string]: boolean };
}

@Injectable({
  providedIn: 'root'
})
export class MessagingService {
  token: string | undefined;
  uid: string | undefined;
  currentMessage = new BehaviorSubject<MessagePayload | null>(null);
  private readonly vapidKey = 'BFg0_T7jFroFWGTrz6YgV9OdYekyARFNfIpWFPYFZSfpAbYoxOyeV2Wn-Wg74YYLjG4Qozfo3wfvSgx1shc31zw';

  constructor(
    private auth: Auth,
    @Optional() private afm: Messaging,
    private afs: Firestore,
    private router: Router,
    private toastController: ToastController,
  ) {
    if (typeof window !== 'undefined' && this.afm) {
      this.receiveMessage();
    }
  }

  updateToken(uid: string, token: string) {
    if (!this.afm) return;
    authState(this.auth).pipe(take(1)).subscribe(user => {
      if (user) {
        const data = { [uid]: token };
        const userRef = doc(this.afs, `ge_users/${uid}`);
        updateDoc(userRef, data);
      }
    });
  }

  private saveToken(user: User, token: string): void {
    if (!this.afm) return;
    const currentTokens = user.tokens || {};
    if (!currentTokens[token]) {
      const userRef = doc(this.afs, `ge_users/${user.uid}`);
      const tokens = { ...currentTokens, [token]: true };
      updateDoc(userRef, { tokens });
    }
  }

  private deleteToken(user: User, token: string): void {
    if (!this.afm) return;
    const userRef = doc(this.afs, `ge_users/${user.uid}`);
    const tokens = { ...user.tokens, [token]: false };
    updateDoc(userRef, { tokens });
  }

  async requestPermission(user: User): Promise<void> {
    if (!this.afm) return;
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const token = await getToken(this.afm, { vapidKey: this.vapidKey });
        if (token) {
          this.saveToken(user, token);
        }
      } else if (typeof Notification !== 'undefined' && Notification.permission !== 'denied') {
        await Notification.requestPermission();
        const token = await getToken(this.afm, { vapidKey: this.vapidKey });
        if (token) {
          this.saveToken(user, token);
        }
      }
    } catch (err) {
      console.error('Unable to get permission to notify.', err);
    }
  }

  async removePermission(user: User): Promise<void> {
    if (!this.afm) return;
    try {
      const token = await getToken(this.afm, { vapidKey: this.vapidKey });
      await this.deleteToken(user, token);
    } catch (err) {
      console.error('Unable to get token to remove.', err);
    }
  }

  async getToken(): Promise<string | null> {
    if (!this.afm) return null;
    try {
      const token = await getToken(this.afm, { vapidKey: this.vapidKey });
      this.token = token;
      return token;
    } catch (err) {
      console.error('Error getting token:', err);
      return null;
    }
  }

  private receiveMessage(): void {
    if (!this.afm) return;
    try {
      const messaging = getMessaging();
      onMessage(messaging, (payload: MessagePayload) => {
        this.currentMessage.next(payload);
        const title = payload.notification?.title || 'New Message';
        const body = payload.notification?.body || '';
        this.presentToast(title, body);
      });
    } catch (err) {
      console.warn('Firebase Messaging is not supported in this environment.', err);
    }
  }

  showMessages(): Observable<MessagePayload | null> {
    if (!this.afm) return of(null);
    return this.currentMessage.asObservable().pipe(
      tap((msg: MessagePayload | null) => {
        if (msg?.notification) {
          const title = msg.notification.title || 'New Message';
          const body = msg.notification.body || '';
          this.presentToast(title, body);
        }
      })
    );
  }

  async presentToast(title: string, body: string): Promise<void> {
    const toast = await this.toastController.create({
      header: title,
      message: body,
      color: 'success',
      duration: 2000,
      position: 'bottom',
      buttons: [
        {
          side: 'end',
          icon: 'md-open',
          handler: () => {
            this.router.navigate(['/account/messages']);
          }
        }
      ]
    });
    await toast.present();
  }
}
